import React from "react"
import { Div, Text } from "atomize"
import Chip from "../atoms/Chip"
import { theme } from "../settings/config"
import {
	FOCUS_AREAS,
	areaItems,
	hasAreaDetail,
	isAreaOn,
	toggleArea,
	toggleAreaItem,
} from "../utils/lessonContent.utils"

// Il pallino delle voci consigliate per il tipo di giocatore scelto
export const RecommendedDot = () => (
	<span
		aria-label="consigliato"
		title="Consigliato"
		style={{
			position: "absolute",
			top: "-3px",
			right: "-3px",
			width: "10px",
			height: "10px",
			borderRadius: "50%",
			background: theme.colors.primary,
			border: `2px solid ${theme.colors.surface}`,
		}}
	/>
)

// Una voce dentro una macroarea: più piccola delle macroaree, e accesa la
// si riconosce dal bordo, come le pastiglie PTR
function ItemChip({ label, isSelected, background, disabled, badge, onClick }) {
	return (
		<button
			type="button"
			aria-pressed={isSelected}
			disabled={disabled}
			onClick={onClick}
			style={{
				position: badge ? "relative" : undefined,
				height: "1.875rem",
				padding: "0 0.7rem",
				borderRadius: "15px",
				border: `2px solid ${isSelected ? theme.colors.gunmetal : background}`,
				background,
				color: isSelected ? theme.colors.gunmetal : theme.colors.text,
				font: "inherit",
				fontSize: "0.8125rem",
				fontWeight: isSelected ? 700 : 400,
				cursor: disabled ? "default" : "pointer",
			}}>
			{label}
			{badge}
		</button>
	)
}

// Su cosa si lavora, per macroaree (FOCUS_AREAS): toccarne una la accende
// con tutte le sue voci, e sotto, nel suo blocco, se ne spegne qualcuna.
// Lo usano il contenuto della lezione e la scheda dell'allievo, che nel
// blocco ci mette anche la nota (`renderExtra`).
//
// `value` ha `strokes` e `situations`, `onChange` riceve la lista che
// cambia. I blocchi hanno il fondo `blockBackground`, le voci dentro
// `itemBackground`: dipende da dove sta il foglio, sul fondo caldo o su un
// riquadro bianco. Senza `withDetail` restano solo le macroaree: a fine
// lezione (il foglio "Com'è andata?") il dettaglio si cambia dal programma.
function FocusAreasPicker({
	value,
	onChange,
	disabled,
	recommended,
	renderExtra,
	blockBackground = theme.colors.surface,
	itemBackground = theme.colors.background,
	withDetail = true,
}) {
	const isRecommended = (area, id) => Boolean(recommended?.[area.list]?.has(id))
	const blocks = withDetail
		? FOCUS_AREAS.filter(area => isAreaOn(value, area) && (hasAreaDetail(area) || renderExtra))
		: []

	return (
		<>
			<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
				{FOCUS_AREAS.map(area => {
					// la tattica chiara, come le situazioni di gioco, per non
					// confonderla coi colpi
					const isTactic = area.list === "situations"
					return (
						<Chip
							key={area.id}
							text={area.label}
							color={isTactic ? theme.colors.gunmetal_lighter : undefined}
							textColor={isTactic ? theme.colors.gunmetal : undefined}
							borderColor={isTactic ? theme.colors.gunmetal : undefined}
							isSelected={isAreaOn(value, area)}
							disabled={disabled}
							badge={area.ids.some(id => isRecommended(area, id)) && <RecommendedDot />}
							onClick={() => onChange(toggleArea(value, area))}
						/>
					)
				})}
			</Div>

			{blocks.length > 0 && (
				<Text
					textSize="caption"
					textWeight="600"
					textColor={theme.colors.text_light}
					m={{ t: "0.75rem" }}>
					Nel dettaglio, se serve
				</Text>
			)}
			{blocks.map(area => (
				<Div
					key={area.id}
					m={{ t: "0.5rem" }}
					p="0.6rem 0.75rem"
					bg={blockBackground}
					rounded="15px"
					d="flex"
					flexDir="column"
					style={{ gap: "0.5rem" }}>
					<Text textWeight="600" textColor={theme.colors.gunmetal}>
						{area.label}
					</Text>
					{hasAreaDetail(area) && (
						<Div
							d="flex"
							flexWrap="wrap"
							role="group"
							aria-label={area.label}
							style={{ gap: "0.375rem" }}>
							{areaItems(area).map(item => (
								<ItemChip
									key={item.id}
									label={item.label}
									isSelected={value[area.list].includes(item.id)}
									background={itemBackground}
									disabled={disabled}
									badge={isRecommended(area, item.id) && <RecommendedDot />}
									onClick={() => onChange(toggleAreaItem(value, area, item.id))}
								/>
							))}
						</Div>
					)}
					{renderExtra?.(area)}
				</Div>
			))}
		</>
	)
}

export default FocusAreasPicker
